import { Sparkle, X, EnvelopeSimple } from "@phosphor-icons/react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast as sonnerToast } from "sonner";

const BETA_DISMISS_KEY = "bmi_beta_banner_dismissed_v1";
const SUPPORT_EMAIL = "support@bookmyinterview.ai";
const MAILTO_SUBJECT = "BMI Beta Feedback";
const MAILTO_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(MAILTO_SUBJECT)}`;

// Iter50.2 — reliable "feedback" click handler.
// mailto: silently no-ops on mobile browsers / desktops without a mail
// client. We open the mailto in a background link AND copy the address
// to the clipboard so the user has a visible fallback either way.
async function handleFeedbackClick(e) {
    // Copy the support email to the clipboard first — always useful.
    let copied = false;
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            copied = true;
        }
    } catch { /* noop */ }

    // Try to open the mail client. If the browser can't handle mailto:,
    // this becomes a no-op (does NOT throw), so the copied clipboard
    // + toast below is the graceful fallback.
    try {
        // Prevent the anchor default so we control window.open focus.
        e.preventDefault();
        const w = window.open(MAILTO_HREF, "_self");
        // Chrome mobile can return null when no handler is registered.
        // The clipboard fallback + toast covers this case.
        void w;
    } catch { /* noop */ }

    sonnerToast.success(
        copied
            ? `Email us at ${SUPPORT_EMAIL} — address copied to clipboard.`
            : `Email us at ${SUPPORT_EMAIL}.`,
        { duration: 6000 },
    );
}

/** Persistent, dismissable "we're in beta" strip that sits above every page.
 *  Uses localStorage so a signed-in user isn't nagged after they've closed it once.
 *
 *  Sprint 2 (2026-02-XX) — initial visible state is now computed
 *  synchronously from localStorage inside `useState(initFn)` so
 *  React's very first render already reflects the correct value.
 *  Previously the banner started `visible=false` and flipped to
 *  `true` in a useEffect, which caused a ~46px downward layout
 *  shift the moment the effect fired — visible on mobile as a
 *  CLS event (measured 0.1148, over the 0.1 target). Reading
 *  synchronously eliminates the shift entirely. Safe: this
 *  component is client-only (CRA has no SSR), so `localStorage`
 *  is always available at first render; the try/catch handles
 *  private-mode / disabled-storage edge cases.
 */
export default function BetaBanner() {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(() => {
        try {
            if (typeof window === "undefined") return false;
            return !localStorage.getItem(BETA_DISMISS_KEY);
        } catch {
            return true;
        }
    });

    // 2026-09 — paid landing routes (/go/*): never show the beta banner
    // to candidates we pay to acquire. Everywhere else unchanged.
    if (pathname.startsWith("/go/")) return null;

    if (!visible) return null;

    return (
        <div
            data-testid="beta-banner"
            className="w-full bg-primary/[0.08] border-b border-primary/20 text-foreground/85"
        >
            <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-[13px]">
                <Sparkle size={14} weight="fill" className="text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <span className="font-medium text-primary">Beta.</span>{" "}
                    <span className="hidden sm:inline">
                        We&apos;re still figuring things out — expect rough edges, small pixels that shouldn&apos;t be where they are, and the occasional confused robot.
                    </span>
                    <span className="sm:hidden">We&apos;re in beta — expect rough edges.</span>{" "}
                    <a
                        href={MAILTO_HREF}
                        onClick={handleFeedbackClick}
                        className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary inline-flex items-center gap-1"
                        data-testid="beta-banner-feedback-link"
                    >
                        <EnvelopeSimple size={12} weight="bold" /> Tell us what broke
                    </a>
                    <span className="text-muted-foreground"> — every bit of feedback helps.</span>
                </div>
                <button
                    onClick={() => {
                        try { localStorage.setItem(BETA_DISMISS_KEY, "1"); } catch { /* noop */ }
                        setVisible(false);
                    }}
                    aria-label="Dismiss beta banner"
                    data-testid="beta-banner-close"
                    className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0"
                >
                    <X size={13} weight="bold" />
                </button>
            </div>
        </div>
    );
}

// ---------- Humorous end-of-flow toast copy ----------
// Small library of "we're in beta, thanks for your patience" messages, indexed
// by lifecycle moment. Consumers call `betaToast(sonner, "jd_generated")` etc.
const BETA_MSGS = {
    jd_generated: {
        title: "JD drafted — feel free to nitpick.",
        body: "This one was written by a very caffeinated AI. If a bit feels off, edit freely. And send us feedback — we&apos;re still tuning the writer.",
    },
    job_published: {
        title: "Your role is LIVE 🎉",
        body: "Now sit back, share the link, and let our robots do the heavy lifting. If something looks weird on the public page — tell us. We&apos;re in beta.",
    },
    application_started: {
        title: "You&apos;re in — let&apos;s get you assessed.",
        body: "Fair warning: our AI screener is friendly but curious. Be yourself, take your time. We&apos;re still in beta, so if any step feels clunky, please tell us.",
    },
    screening_pass: {
        // Iter68 copy fix — the old "You cleared the screening 🎉" title
        // fired the exact bug the tester reported: candidates read
        // "cleared" before AV was actually submitted. This banner variant
        // is only ever rendered from an outcome-gated context, but the
        // copy itself now doesn't presuppose that so a future accidental
        // trigger cannot mislead.
        title: "Ready to book your interview 🎉",
        body: "Pick a slot below to lock it in. We&apos;re in beta — if the calendar acts weird, hit refresh and try again. Sorry in advance!",
    },
    screening_fail: {
        title: "Thanks for taking the time.",
        body: "This one wasn&apos;t a match, but your profile is saved for future matches. Beta reminder: if you think our AI got this wrong, please write to us — feedback shapes the next version.",
    },
    interview_booked: {
        title: "Interview locked in ✅",
        body: "You&apos;ll get a confirmation email + a reminder 24h and 1h before. We&apos;re still in beta so if anything looks fishy in the email — that&apos;s on us, not you. Ping support@bookmyinterview.ai.",
    },
    interview_cancelled: {
        title: "Interview released.",
        body: "The candidate has been notified. Beta note: if the email didn&apos;t look quite right, please let us know — we&apos;re tuning the templates weekly.",
    },
    interview_cancelled_candidate: {
        title: "Slot released — you can rebook.",
        body: "Sorry for the shuffle. Head back to the results page and pick a new time. We&apos;re still in beta — thank you for rolling with us!",
    },
    profile_saved: {
        title: "Saved.",
        body: "Refresh the public job page to see the new brand kit in action. If a field didn&apos;t stick — tell us, we&apos;re in beta.",
    },
    logo_uploaded: {
        title: "Logo updated.",
        body: "It&apos;ll show up on every job page and social preview from now on. Fingers crossed it looks great — if it doesn&apos;t, upload a square-ish PNG for best results.",
    },
    cv_uploaded: {
        title: "CV parsed.",
        body: "We&apos;ve pulled out your skills — feel free to tidy the list. Beta note: our parser learns weekly, so if it missed a skill, please tell us.",
    },
    generic_success: {
        title: "Done — thanks for your patience.",
        body: "We&apos;re still in beta. If anything feels off, support@bookmyinterview.ai is a real inbox with real humans.",
    },
};

export function betaToastCopy(key) {
    return BETA_MSGS[key] || BETA_MSGS.generic_success;
}

/** Fires a Sonner toast with the humorous beta message for the given lifecycle moment.
 *  Pass the imported `toast` function so this module stays UI-agnostic.
 */
export function betaToast(toast, key, opts = {}) {
    const msg = betaToastCopy(key);
    toast.success(msg.title, {
        description: msg.body.replaceAll("&apos;", "'"),
        duration: opts.duration ?? 6000,
        ...opts,
    });
}
